import React, { useCallback, useEffect, useState } from 'react';
import { styled } from '@mui/system';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

export interface CarouselProps {
  images: string[];
  children?: React.ReactNode;
}

const Carousel = (props: CarouselProps) => {
  const { images, children } = props;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const prevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    } else {
      setCurrentImageIndex(images.length - 1);
    }
  };

  const [isTransitioning, setIsTransitioning] = useState(false);

  const nextImage = useCallback(() => {
    setIsTransitioning(true);
    setTimeout(() => {
      if (currentImageIndex < images.length - 1) {
        setCurrentImageIndex(currentImageIndex + 1);
      } else {
        setCurrentImageIndex(0);
      }
      setIsTransitioning(false);
    }, 200); // Matches transition duration
  }, [currentImageIndex, images.length]);

  useEffect(() => {
    const interval = setInterval(nextImage, 5000);
    return () => clearInterval(interval);
  }, [currentImageIndex, nextImage]);

  return (
    <CarouselWrapper>
      <SwitchTransition>
        <CSSTransition
          key={images[currentImageIndex]}
          timeout={500}
          classNames={{
            enter: 'carousel-fade-enter',
            enterActive: 'carousel-fade-enter-active',
            exit: 'carousel-fade-exit',
            exitActive: 'carousel-fade-exit-active',
          }}
        >
          <CarouselImage bgImage={images[currentImageIndex]} />
        </CSSTransition>
      </SwitchTransition>

      {/* rest of your buttons and dots */}
    </CarouselWrapper>
  );
};

export default Carousel;

const CarouselWrapper = styled('div')({
  width: '100%',
  height: '100vh',
  position: 'relative',

  '@media (max-width: 600px)': {
    height: '920px',
  },
});

const Content = styled('div')({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '10px',

  '@media (max-width: 950px)': {
    padding: '0px',
  },
});

const CarouselImage = styled('div', {
  shouldForwardProp: (prop) => prop !== 'bgImage',
})<{
  bgImage: string;
}>(({ theme, bgImage }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(${bgImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
}));

const CarouselLeft = styled('div')({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '25%',
  cursor: 'pointer',
  left: 0,
});

const CarouselRight = styled('div')({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '25%',
  cursor: 'pointer',
  right: 0,
});

const CarouselDots = styled('div')({
  position: 'absolute',
  bottom: 10,
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
});

const CarouselDot = styled('span')<{
  active: boolean;
}>(({ theme, active }) => ({
  width: 10,
  height: 10,
  background: active ? 'lightgray' : 'white',
  borderRadius: '50%',
  margin: '0 5px',
  cursor: 'pointer',
}));

const CarouselTransitionOverlay = styled('div')((props) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'white',
  transition: 'opacity 0.2s',
  opacity: 1,
}));

const style = document.createElement("style");
style.innerHTML = `
  .carousel-fade-enter {
    opacity: 0;
    z-index: 1;
  }
  .carousel-fade-enter-active {
    opacity: 1;
    transition: opacity 0.5s ease-in;
  }
  .carousel-fade-exit {
    opacity: 1;
  }
  .carousel-fade-exit-active {
    opacity: 0;
    transition: opacity 0.5s ease-out;
  }
`;
document.head.appendChild(style);
